package top.wcpe.allincore.dsl.ir.expr

import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import kotlin.jvm.JvmField
import kotlin.jvm.JvmOverloads

/**
 * 语句节点。
 */
interface StmtNode {

    /** 源码行号（1-based），0 表示未知。 */
    val line: Int get() = 0

    /** 源码列号（1-based），0 表示未知。 */
    val column: Int get() = 0

    /** `state.<name> = rhs` 赋值。 */
    class Assign @JvmOverloads constructor(
        stateName: String?,
        rhs: ExprNode?,
        // 源码位置信息
        override val line: Int = 0,
        override val column: Int = 0,
    ) : StmtNode {
        val stateName: String = stateName ?: throw NullPointerException()
        val rhs: ExprNode = rhs ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is Assign) return false
            return stateName == other.stateName && rhs == other.rhs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + stateName.hashCode()
            result = 31 * result + rhs.hashCode()
            return result
        }

        override fun toString(): String = "Assign(" + stateName + " = " + rhs + ")"
    }

    /** `session.<name> = rhs` 赋值（写到全局 SharedStateRegistry）。 */
    class SessionAssign @JvmOverloads constructor(
        fieldName: String?,
        rhs: ExprNode?,
        override val line: Int = 0,
        override val column: Int = 0,
    ) : StmtNode {
        val fieldName: String = fieldName ?: throw NullPointerException()
        val rhs: ExprNode = rhs ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is SessionAssign) return false
            return fieldName == other.fieldName && rhs == other.rhs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + fieldName.hashCode()
            result = 31 * result + rhs.hashCode()
            return result xor 0x53455353
        }

        override fun toString(): String = "SessionAssign(" + fieldName + " = " + rhs + ")"
    }

    /** `persist.<name> = rhs` 赋值（写到 PersistentStateStore，玩家退出时落盘）。 */
    class PersistAssign @JvmOverloads constructor(
        fieldName: String?,
        rhs: ExprNode?,
        override val line: Int = 0,
        override val column: Int = 0,
    ) : StmtNode {
        val fieldName: String = fieldName ?: throw NullPointerException()
        val rhs: ExprNode = rhs ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is PersistAssign) return false
            return fieldName == other.fieldName && rhs == other.rhs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + fieldName.hashCode()
            result = 31 * result + rhs.hashCode()
            return result xor 0x50455253
        }

        override fun toString(): String = "PersistAssign(" + fieldName + " = " + rhs + ")"
    }

    /**
     * `request <namespace>.<handler>(payload) { pending {} success(p) {} fail(e) {} timeout {} }`。
     *
     * [isNetMode] 为 true 时表示由 `net.request(...)` 单回调形态 lower 而来：
     * 4 个回调体共享同一个用户 lambda/FunRef，回调触发时由 runtime 合成
     * `{kind, payload}` 结果 Map 注入到 paramName 所指 var。
     */
    class Request(
        namespace: String?,
        handler: String?,
        payload: List<PayloadEntry>?,
        callbacks: List<Callback>?,
        val isNetMode: Boolean,
        /** 可空：为 null 表示采用默认超时。 */
        val timeoutMs: ExprNode?,
        // 源码位置信息
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val namespace: String = namespace ?: throw NullPointerException()
        val handler: String = handler ?: throw NullPointerException()
        val payload: List<PayloadEntry> = ReadOnlyList(ArrayList(payload ?: throw NullPointerException()))
        val callbacks: List<Callback> = ReadOnlyList(ArrayList(callbacks ?: throw NullPointerException()))

        constructor(
            namespace: String?,
            handler: String?,
            payload: List<PayloadEntry>?,
            callbacks: List<Callback>?,
        ) : this(namespace, handler, payload, callbacks, false, null, 0, 0)

        constructor(
            namespace: String?,
            handler: String?,
            payload: List<PayloadEntry>?,
            callbacks: List<Callback>?,
            netMode: Boolean,
        ) : this(namespace, handler, payload, callbacks, netMode, null, 0, 0)


        override fun equals(other: Any?): Boolean {
            if (other !is Request) return false
            return isNetMode == other.isNetMode && namespace == other.namespace &&
                handler == other.handler && payload == other.payload &&
                callbacks == other.callbacks && timeoutMs == other.timeoutMs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + namespace.hashCode()
            result = 31 * result + handler.hashCode()
            result = 31 * result + payload.hashCode()
            result = 31 * result + callbacks.hashCode()
            result = 31 * result + isNetMode.hashCode()
            result = 31 * result + (timeoutMs?.hashCode() ?: 0)
            return result
        }

        override fun toString(): String =
            "Request(" + (if (isNetMode) "net " else "") + namespace + "." + handler +
                ", payload=" + payload + ", callbacks=" + callbacks +
                (if (timeoutMs == null) "" else ", timeoutMs=" + timeoutMs) + ")"

        /** payload 单项：`key = expr`，按声明顺序保留。 */
        class PayloadEntry(key: String?, value: ExprNode?) {
            val key: String = key ?: throw NullPointerException()
            val value: ExprNode = value ?: throw NullPointerException()

            override fun equals(other: Any?): Boolean {
                if (other !is PayloadEntry) return false
                return key == other.key && value == other.value
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + key.hashCode()
                result = 31 * result + value.hashCode()
                return result
            }

            override fun toString(): String = "$key=$value"
        }

        /** 回调块。pending/timeout 无参数（[paramName] 为 null）。 */
        class Callback(kind: Kind?, paramName: String?, body: List<StmtNode>?) {
            /** 回调种类。 */
            enum class Kind {
                PENDING, SUCCESS, FAIL, TIMEOUT
            }

            val kind: Kind = kind ?: throw NullPointerException()

            /** success/fail 的参数名；pending/timeout 时为 null。 */
            val paramName: String? = paramName // pending/timeout 允许 null
            val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))

            override fun equals(other: Any?): Boolean {
                if (other !is Callback) return false
                return kind == other.kind && paramName == other.paramName && body == other.body
            }

            override fun hashCode(): Int {
                var result = 1
                result = 31 * result + kind.hashCode()
                result = 31 * result + (paramName?.hashCode() ?: 0)
                result = 31 * result + body.hashCode()
                return result
            }

            override fun toString(): String =
                "Callback(" + kind + (if (paramName == null) "" else "(" + paramName + ")") +
                    ", body=" + body + ")"
        }
    }

    /**
     * 表达式语句：把一个调用表达式作为语句执行，丢弃返回值。
     *
     * [call] 仅允许是 [ExprNode.CallExpr] 或 [ExprNode.MemberCall]；
     * 其它形式由序列化层保证不会出现。
     */
    class ExprStmt @JvmOverloads constructor(
        call: ExprNode?,
        // 源码位置信息
        override val line: Int = 0,
        override val column: Int = 0,
    ) : StmtNode {
        val call: ExprNode = call ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean = other is ExprStmt && call == other.call

        override fun hashCode(): Int = call.hashCode()

        override fun toString(): String = "ExprStmt(" + call + ")"
    }

    // ===== C1 新增语句 =====

    /** 局部变量赋值（针对 LocalLValue），与 [Assign] 平行。 */
    class LocalAssign(name: String?, rhs: ExprNode?, override val line: Int, override val column: Int) : StmtNode {
        val name: String = name ?: throw NullPointerException()
        val rhs: ExprNode = rhs ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is LocalAssign) return false
            return name == other.name && rhs == other.rhs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + rhs.hashCode()
            return result
        }

        override fun toString(): String = "LocalAssign(" + name + " = " + rhs + ")"
    }

    /**
     * M6-a：索引位置赋值 `target[index] = rhs`。
     * - List：index 为整数；越界写入由 runtime 抛错
     * - Map：index 为字符串；缺失 key 直接插入
     */
    class IndexAssign @JvmOverloads constructor(
        target: ExprNode?,
        index: ExprNode?,
        rhs: ExprNode?,
        // 源码位置信息
        override val line: Int = 0,
        override val column: Int = 0,
    ) : StmtNode {
        val target: ExprNode = target ?: throw NullPointerException()
        val index: ExprNode = index ?: throw NullPointerException()
        val rhs: ExprNode = rhs ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is IndexAssign) return false
            return target == other.target && index == other.index && rhs == other.rhs
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + target.hashCode()
            result = 31 * result + index.hashCode()
            result = 31 * result + rhs.hashCode()
            return result
        }

        override fun toString(): String = "IndexAssign(" + target + "[" + index + "] = " + rhs + ")"
    }

    /** `var name = init` 局部变量声明。 */
    class VarDecl(name: String?, initializer: ExprNode?, override val line: Int, override val column: Int) : StmtNode {
        val name: String = name ?: throw NullPointerException()
        val initializer: ExprNode = initializer ?: throw NullPointerException()


        override fun equals(other: Any?): Boolean {
            if (other !is VarDecl) return false
            return name == other.name && initializer == other.initializer
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + initializer.hashCode()
            return result
        }

        override fun toString(): String = "VarDecl(" + name + " = " + initializer + ")"
    }

    /** `if (cond) {then} else {else}`。 */
    class If(
        cond: ExprNode?,
        thenBody: List<StmtNode>?,
        elseBody: List<StmtNode>?,
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val cond: ExprNode = cond ?: throw NullPointerException()
        val thenBody: List<StmtNode> = ReadOnlyList(ArrayList(thenBody ?: throw NullPointerException()))
        val elseBody: List<StmtNode> = ReadOnlyList(ArrayList(elseBody ?: throw NullPointerException()))


        override fun equals(other: Any?): Boolean {
            if (other !is If) return false
            return cond == other.cond && thenBody == other.thenBody && elseBody == other.elseBody
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + cond.hashCode()
            result = 31 * result + thenBody.hashCode()
            result = 31 * result + elseBody.hashCode()
            return result
        }
    }

    /** `while (cond) {body}`。 */
    class While(cond: ExprNode?, body: List<StmtNode>?, override val line: Int, override val column: Int) : StmtNode {
        val cond: ExprNode = cond ?: throw NullPointerException()
        val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))


        override fun equals(other: Any?): Boolean {
            if (other !is While) return false
            return cond == other.cond && body == other.body
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + cond.hashCode()
            result = 31 * result + body.hashCode()
            return result
        }
    }

    /**
     * `break`。
     *
     * 设计取舍：Break/Continue 为单例以节省堆对象。wire 中每条 stmt 前仍有 position 前缀，
     * reader 会读取但丢弃（[ExprBlobReader] 读到 STMT_BREAK/CONTINUE 后直接返回单例）。
     * 若未来需要在循环外越界场景报告源位置，需改为非单例实现。
     */
    class Break private constructor() : StmtNode {
        override fun equals(other: Any?): Boolean = other is Break

        override fun hashCode(): Int = 1

        companion object {
            @JvmField
            val INSTANCE = Break()
        }
    }

    /** `continue`。设计取舍同 [Break]。 */
    class Continue private constructor() : StmtNode {
        override fun equals(other: Any?): Boolean = other is Continue

        override fun hashCode(): Int = 2

        companion object {
            @JvmField
            val INSTANCE = Continue()
        }
    }

    /** `return [expr]`。[value] 为 null 表示裸 return。 */
    class Return(val value: ExprNode?, override val line: Int, override val column: Int) : StmtNode {

        override fun equals(other: Any?): Boolean {
            if (other !is Return) return false
            return value == other.value
        }

        override fun hashCode(): Int = value?.hashCode() ?: 0
    }

    /** `try { } catch (name) { } [finally { }]`。 */
    class Try(
        tryBody: List<StmtNode>?,
        catchName: String?,
        catchBody: List<StmtNode>?,
        finallyBody: List<StmtNode>?,
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val tryBody: List<StmtNode> = ReadOnlyList(ArrayList(tryBody ?: throw NullPointerException()))
        val catchName: String = catchName ?: throw NullPointerException()
        val catchBody: List<StmtNode> = ReadOnlyList(ArrayList(catchBody ?: throw NullPointerException()))
        val finallyBody: List<StmtNode> = ReadOnlyList(ArrayList(finallyBody ?: throw NullPointerException()))


        override fun equals(other: Any?): Boolean {
            if (other !is Try) return false
            return tryBody == other.tryBody && catchName == other.catchName &&
                catchBody == other.catchBody && finallyBody == other.finallyBody
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + tryBody.hashCode()
            result = 31 * result + catchName.hashCode()
            result = 31 * result + catchBody.hashCode()
            result = 31 * result + finallyBody.hashCode()
            return result
        }
    }

    // ===== C2 新增语句 =====

    /** `for (name in iterable) { body }`。 */
    class ForIn(
        varName: String?,
        iterable: ExprNode?,
        body: List<StmtNode>?,
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val varName: String = varName ?: throw NullPointerException()
        val iterable: ExprNode = iterable ?: throw NullPointerException()
        val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))


        override fun equals(other: Any?): Boolean {
            if (other !is ForIn) return false
            return varName == other.varName && iterable == other.iterable && body == other.body
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + varName.hashCode()
            result = 31 * result + iterable.hashCode()
            result = 31 * result + body.hashCode()
            return result
        }
    }

    /** 语句形态的 when：复用 [ExprNode.When] 结构。 */
    class WhenStmt(whenNode: ExprNode.When?, override val line: Int, override val column: Int) : StmtNode {
        val whenNode: ExprNode.When = whenNode ?: throw NullPointerException()

        /** 保持原 Java getter 名 getWhen()。 */
        fun getWhen(): ExprNode.When = whenNode


        override fun equals(other: Any?): Boolean = other is WhenStmt && whenNode == other.whenNode

        override fun hashCode(): Int = whenNode.hashCode()
    }

    // ===== D2-b 新增：类型化状态机（IR v8） =====

    /**
     * 顶层状态机节点（替代 D1 的 AwaitLoweredStmt）。
     *
     * 语义：进入时从 `blocks[entryStateId]` 开始执行，遇 await 切换状态、
     * 遇 terminal 退出。每个 var 在编译期固定一个槽位（[recordSchemaNames]
     * 与 [recordSchemaTypes] 同长度并对齐）。
     */
    class StateMachine(
        recordSchemaNames: List<String>?,
        recordSchemaTypes: List<IrType>?,
        val entryStateId: Int,
        blocks: List<StateBlock>?,
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val recordSchemaNames: List<String> =
            ReadOnlyList(ArrayList(recordSchemaNames ?: throw NullPointerException()))
        val recordSchemaTypes: List<IrType> =
            ReadOnlyList(ArrayList(recordSchemaTypes ?: throw NullPointerException()))
        val blocks: List<StateBlock> = ReadOnlyList(ArrayList(blocks ?: throw NullPointerException()))

        init {
            require(this.recordSchemaNames.size == this.recordSchemaTypes.size) {
                "state record schema 名称/类型数量不一致"
            }
        }

    }

    /**
     * 单个状态块：一组同步语句 + 终结符（transition / terminal_natural / terminal_return）。
     */
    class StateBlock(
        val blockId: Int,
        stmts: List<StmtNode>?,
        val terminatorKind: Int,
        /** terminator=transition 时非 null。 */
        val transition: Transition?,
        /** terminator=terminal_return 且有返回值时非 null。 */
        val returnValue: ExprNode?,
        /** terminator=goto 时的目标状态号；其它形态为 -1。 */
        val gotoNextStateId: Int,
        /** terminator=branch 时的条件表达式；其它形态为 null。 */
        val branchCond: ExprNode?,
        val branchThenStateId: Int,
        val branchElseStateId: Int,
    ) {
        val stmts: List<StmtNode> = ReadOnlyList(ArrayList(stmts ?: throw NullPointerException()))

        /** terminator=delay 时的延迟毫秒数表达式；其它形态为 null。 */
        var delayMs: ExprNode? = null

        /** 兼容旧 5 参数构造器：仅供 transition / natural / return 使用。 */
        constructor(
            blockId: Int,
            stmts: List<StmtNode>?,
            terminatorKind: Int,
            transition: Transition?,
            returnValue: ExprNode?,
        ) : this(blockId, stmts, terminatorKind, transition, returnValue, -1, null, -1, -1)

        /** terminator=delay 时的下一状态号（复用 gotoNextStateId 字段）。 */
        val delayNextStateId: Int get() = gotoNextStateId

        companion object {
            /**
             * terminator 编码（位于 STATE_BLOCK 内嵌子标签空间；不新增独立 STMT wire tag）：
             * 0=transition, 1=terminal_natural, 2=terminal_return, 3=goto, 4=branch。
             *
             * D2-c 引入 GOTO / BRANCH 以表达控制流内 await 的状态跳转：
             * GOTO 无条件跳到 gotoNextStateId；
             * BRANCH 对 branchCond 求值，按布尔分流到 branchThenStateId / branchElseStateId。
             */
            const val TERM_TRANSITION = 0
            const val TERM_NATURAL = 1
            const val TERM_RETURN = 2
            const val TERM_GOTO = 3
            const val TERM_BRANCH = 4
            const val TERM_DELAY = 5
        }
    }

    /**
     * 状态切换：与 D1 await net.request 等价的 transition payload。
     *
     * M4 A2 Stage 2 引入 [KIND_SUSPEND_CALL] 形态用于"await suspend lambda 调用"，
     * 此时 namespace/handler/payload/timeoutMs 为空字段，[lambdaCallee]/[args]
     * 承载真正的调用目标。Stage 2 runtime 不消费 kind=1，executor 见到直接抛"未实现"。
     */
    class Transition {
        val transitionKind: Int

        // ---- kind=0 (NetRequest) 字段 ----
        val namespace: String
        val handler: String
        val payload: List<Request.PayloadEntry>

        /** 可空：为 null 表示采用默认超时。kind=1 时始终为 null。 */
        val timeoutMs: ExprNode?

        // ---- kind=1 (SuspendCall) 字段 ----
        /** kind=1：suspend lambda 的 callee 表达式；kind=0 时为 null。 */
        val lambdaCallee: ExprNode?

        /** kind=1：调用参数；kind=0 时为空表（不允许 null，便于 getter 一致）。 */
        val args: List<ExprNode>

        // ---- 公共字段 ----
        val successNextStateId: Int
        val failureNextStateId: Int
        val timeoutNextStateId: Int

        /** 可空：success 投递时把 result（或 tailExpr 求值）绑到该 var。 */
        val bindVarName: String?

        /** 可空：success 投递的"后缀链表达式"，对 __result 求值得到最终值。kind=1 时强制为 null。 */
        val tailExpr: ExprNode?

        /** kind=0 旧形态构造器（保持调用点兼容）。 */
        constructor(
            namespace: String?,
            handler: String?,
            payload: List<Request.PayloadEntry>?,
            timeoutMs: ExprNode?,
            successNextStateId: Int,
            failureNextStateId: Int,
            timeoutNextStateId: Int,
            bindVarName: String?,
            tailExpr: ExprNode?,
        ) {
            this.transitionKind = KIND_NET_REQUEST
            this.namespace = namespace ?: throw NullPointerException()
            this.handler = handler ?: throw NullPointerException()
            this.payload = ReadOnlyList(ArrayList(payload ?: throw NullPointerException()))
            this.timeoutMs = timeoutMs
            this.lambdaCallee = null
            this.args = emptyList()
            this.successNextStateId = successNextStateId
            this.failureNextStateId = failureNextStateId
            this.timeoutNextStateId = timeoutNextStateId
            this.bindVarName = bindVarName
            this.tailExpr = tailExpr
        }

        /** kind=1 SuspendCall 形态构造器（Stage 3 才会生成）。 */
        constructor(
            lambdaCallee: ExprNode?,
            args: List<ExprNode>?,
            successNextStateId: Int,
            failureNextStateId: Int,
            timeoutNextStateId: Int,
            bindVarName: String?,
        ) {
            this.transitionKind = KIND_SUSPEND_CALL
            this.namespace = ""
            this.handler = ""
            this.payload = emptyList()
            this.timeoutMs = null
            this.lambdaCallee = lambdaCallee ?: throw NullPointerException()
            this.args = ReadOnlyList(ArrayList(args ?: throw NullPointerException()))
            this.successNextStateId = successNextStateId
            this.failureNextStateId = failureNextStateId
            this.timeoutNextStateId = timeoutNextStateId
            this.bindVarName = bindVarName
            this.tailExpr = null
        }

        companion object {
            /** kind=0：旧形态，await net.request(...)。 */
            const val KIND_NET_REQUEST = 0

            /** kind=1：M4 A2 Stage 3 形态，await suspend-lambda 调用（Stage 2 仅 wire / AST 层引入）。 */
            const val KIND_SUSPEND_CALL = 1
        }
    }

    /**
     * 局部 fun 声明 `fun NAME(params) { body }`（C4）。
     *
     * 与 lambda 的"按值闭包"不同：执行时不创建新栈，直接在当前 EvalContext 帧栈上
     * push 参数帧执行 body，从而实现"按引用"捕获外层 var。
     */
    class LocalFunDecl(
        name: String?,
        params: List<String>?,
        body: List<StmtNode>?,
        defaults: List<ExprNode?>?,
        override val line: Int,
        override val column: Int,
    ) : StmtNode {
        val name: String = name ?: throw NullPointerException()
        val params: List<String> = ReadOnlyList(ArrayList(params ?: throw NullPointerException()))
        val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))

        /** M5-b：默认值表达式列表，与 [params] 等长，元素 null 表示无默认值；空列表表示全部无默认值。 */
        val defaults: List<ExprNode?>

        init {
            val d = defaults ?: throw NullPointerException("defaults")
            require(d.isEmpty() || d.size == this.params.size) {
                "局部 fun '" + this.name + "' defaults 长度与 params 不一致"
            }
            this.defaults = ReadOnlyList(ArrayList(d))
        }

        constructor(name: String?, params: List<String>?, body: List<StmtNode>?) :
            this(name, params, body, emptyList(), 0, 0)


        override fun equals(other: Any?): Boolean {
            if (other !is LocalFunDecl) return false
            return name == other.name && params == other.params &&
                body == other.body && defaults == other.defaults
        }

        override fun hashCode(): Int {
            var result = 1
            result = 31 * result + name.hashCode()
            result = 31 * result + params.hashCode()
            result = 31 * result + body.hashCode()
            result = 31 * result + defaults.hashCode()
            return result
        }

        override fun toString(): String = "LocalFunDecl(" + name + params + ")"
    }

    /**
     * `batch { stmts }` 合批写入语句。
     *
     * 运行时等价于 `BatchScope.enter()` + 执行 body + `BatchScope.exit()`，
     * 使块内多次响应式写入合并为一次订阅者通知。本类仅为数据结构，不含运行时引用。
     */
    class Batch(body: List<StmtNode>?, override val line: Int, override val column: Int) : StmtNode {
        val body: List<StmtNode> = ReadOnlyList(ArrayList(body ?: throw NullPointerException()))


        override fun equals(other: Any?): Boolean = other is Batch && body == other.body

        override fun hashCode(): Int = body.hashCode()

        override fun toString(): String = "Batch(" + body + ")"
    }
}
